import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import axios from "axios";
import { Tooltip } from "antd";
import { api_v3_leaderboard, api_v3_list_of_clans, api_v3_sync_history, api_v3_sync_kill, api_v3_sync_force_cancel, api_v3_jobs_unlock, api_v3_sync_delete_all, comunidades } from "../utils/constants";
import { useNavContext } from "../context/nav_context";
import { Loading } from "../components";

const SORT_FIELDS = [
  { key: "avgElo", label: "Rating" },
  { key: "currentElo", label: "Elo actual" },
  { key: "eloChange24h", label: "Elo 24h" },
  { key: "eloChange7d", label: "Elo 7d" },
];

const TOOLTIPS = {
  avgElo: "Media de los últimos 50 1v1 y Elo máximo, dividido entre 2",
  currentElo: "Elo actual y Elo máximo histórico alcanzado",
  eloChange24h: "Cambio de Elo en las últimas 24 horas",
  eloChange7d: "Cambio de Elo en los últimos 7 días",
};

const getInitialAvatar = (name) => {
  const initial = (name || "?")[0].toUpperCase();
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28">
    <rect width="28" height="28" rx="14" fill="#3a3a4a"/>
    <text x="14" y="14" text-anchor="middle" dominant-baseline="central" fill="rgba(255,255,255,0.6)" font-family="sans-serif" font-size="14" font-weight="600">${initial}</text>
  </svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

const fmtRelativeTime = (isoDate) => {
  if (!isoDate) return "—";
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const diffH = Math.floor(diffMs / 3600000);
  const diffD = Math.floor(diffMs / 86400000);
  if (diffMs < 0) return "—";
  if (diffH < 1) return "<1h";
  if (diffH < 24) return `hace ${diffH}h`;
  if (diffD < 7) return `hace ${diffD}d`;
  return `hace ${Math.floor(diffD / 7)}sem`;
};

const LeaderboardPage = () => {
  const { user } = useNavContext();

  const [entries, setEntries] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [sortField, setSortField] = useState("avgElo");
  const [sortDir, setSortDir] = useState("desc");

  const [clans, setClans] = useState([]);
  const [selectedClanId, setSelectedClanId] = useState(null);
  const [selectedComunidadId, setSelectedComunidadId] = useState(null);
  const [openPopover, setOpenPopover] = useState(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  const [debugOpen, setDebugOpen] = useState(false);
  const [syncHistory, setSyncHistory] = useState([]);
  const [syncHistoryLoading, setSyncHistoryLoading] = useState(false);
  const [now, setNow] = useState(Date.now());

  const popoverRef = useRef(null);
  const syncRefreshRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setOpenPopover(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setError(false);
      try {
        const [lbRes, clansRes] = await Promise.all([
          axios.get(api_v3_leaderboard()),
          axios.get(api_v3_list_of_clans()),
        ]);
        if (!cancelled) {
          const lbData = lbRes.data || {};
          setEntries(lbData.items || []);
          setMeta(lbData.meta || null);

          if (clansRes.status === 200) {
            const filtered = clansRes.data.filter(
              (c) => c.name !== "<No tengo clan>" && c.name !== "Otro clan"
            );
            const sorted = filtered.sort((a, b) =>
              a.name.localeCompare(b.name)
            );
            setClans(sorted);
          }
        }
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    if (!debugOpen) return;
    const doFetch = () => {
      const token = localStorage.getItem("access_token");
      axios.get(api_v3_sync_history(50), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
        .then((res) => setSyncHistory(res.data || []))
        .catch(() => {});
    };
    syncRefreshRef.current = setInterval(doFetch, 60000);
    return () => clearInterval(syncRefreshRef.current);
  }, [debugOpen]);

  const fmtLocalTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso.slice(0, 19).replace("T", " ");
    const pad = (n) => String(n).padStart(2, "0");
    const parts = new Intl.DateTimeFormat("es", {
      timeZone: "Europe/Madrid",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      hourCycle: "h23",
    }).formatToParts(d);
    const get = (type) => parts.find((p) => p.type === type).value;
    return `${get("day")}/${get("month")}/${get("year")} ${get("hour")}:${get("minute")}:${get("second")}`;
  };

  const fmtDuration = (seconds) => {
    if (seconds == null || isNaN(seconds)) return "—";
    const totalSec = Math.round(seconds);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  const runningElapsed = (h) => {
    if (h.elapsedSeconds != null) return fmtDuration(h.elapsedSeconds);
    if (!h.startedAt) return "—";
    const elapsed = (now - new Date(h.startedAt).getTime()) / 1000;
    return fmtDuration(elapsed);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const togglePopover = useCallback((name) => {
    setOpenPopover((prev) => (prev === name ? null : name));
  }, []);

  const handleClanClick = (clanId) => {
    setSelectedClanId((prev) => (prev === clanId ? null : clanId));
    setOpenPopover(null);
  };

  const handleComunidadClick = (comunidadId) => {
    setSelectedComunidadId((prev) => (prev === comunidadId ? null : comunidadId));
    setOpenPopover(null);
  };

  const handleKillJob = async (recordId) => {
    if (!window.confirm("¿Estás seguro de cancelar esta ejecución?")) return;
    const token = localStorage.getItem("access_token");
    try {
      await axios.post(
        api_v3_sync_kill(recordId),
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      setSyncHistory((prev) =>
        prev.map((h) =>
          h.id === recordId ? { ...h, killRequested: true } : h
        )
      );
    } catch {
      alert("Error al solicitar el kill");
    }
  };

  const handleForceCancel = async (recordId) => {
    if (!window.confirm("¿Forzar cancelación? (el job no responderá)")) return;
    const token = localStorage.getItem("access_token");
    try {
      const res = await axios.post(
        api_v3_sync_force_cancel(recordId),
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (res.status === 200) {
        setSyncHistory((prev) =>
          prev.map((h) =>
            h.id === recordId
              ? { ...h, status: "canceled", killRequested: true, elapsedSeconds: 0 }
              : h
          )
        );
      }
    } catch {
      alert("Error al forzar cancelación");
    }
  };

  const handleUnlock = async () => {
    if (!window.confirm("¿Liberar el lock? (solo si no hay ningún job ejecutándose)")) return;
    const token = localStorage.getItem("access_token");
    try {
      await axios.post(
        api_v3_jobs_unlock(),
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      alert("Lock liberado");
    } catch {
      alert("Error al liberar el lock");
    }
  };

  const handleDeleteHistory = async () => {
    if (!window.confirm("¿Borrar todo el historial de sync? Esta acción no se puede deshacer.")) return;
    const token = localStorage.getItem("access_token");
    try {
      await axios.delete(
        api_v3_sync_delete_all(),
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      setSyncHistory([]);
      alert("Historial borrado");
    } catch {
      alert("Error al borrar el historial");
    }
  };

  const handleClear = () => {
    setSelectedClanId(null);
    setSelectedComunidadId(null);
  };

  const sortedEntries = useMemo(() => {
    const sorted = [...entries].sort((a, b) => {
      const va = a[sortField] ?? -1;
      const vb = b[sortField] ?? -1;
      return sortDir === "desc" ? vb - va : va - vb;
    });
    return sorted;
  }, [entries, sortField, sortDir]);

  const filteredEntries = useMemo(() => {
    return sortedEntries.filter((e) => {
      const matchClan = !selectedClanId || e.clanId === selectedClanId;
      const matchComunidad = !selectedComunidadId || e.comunidadAutonoma === selectedComunidadId;
      return matchClan && matchComunidad;
    });
  }, [sortedEntries, selectedClanId, selectedComunidadId]);

  const insights = useMemo(() => {
    const with7d = entries.filter(e => e.eloChange7d != null && e.eloChange7d !== 0);
    const with24h = entries.filter(e => e.eloChange24h != null && e.eloChange24h !== 0);
    return {
      gainers7d: [...with7d].sort((a, b) => b.eloChange7d - a.eloChange7d).slice(0, 3),
      losers7d:  [...with7d].sort((a, b) => a.eloChange7d - b.eloChange7d).slice(0, 3),
      gainer24h: [...with24h].sort((a, b) => b.eloChange24h - a.eloChange24h).slice(0, 1),
      loser24h:  [...with24h].sort((a, b) => a.eloChange24h - b.eloChange24h).slice(0, 1),
    };
  }, [entries]);

  const selectedClan = selectedClanId
    ? clans.find((c) => c.clanId === selectedClanId)
    : null;

  const selectedComunidad = selectedComunidadId
    ? comunidades.find((c) => c.id === selectedComunidadId)
    : null;

  if (loading) {
    return (
      <Canvas>
        <Loading />
      </Canvas>
    );
  }

  if (error) {
    return (
      <Canvas>
        <EmptyState>Ha ocurrido un error cargando el ranking.</EmptyState>
      </Canvas>
    );
  }

  if (entries.length === 0) {
    return (
      <Canvas>
        <EmptyState>No hay jugadores con datos de elo disponibles.</EmptyState>
      </Canvas>
    );
  }

  const hasFilters = selectedClanId || selectedComunidadId;

  return (
    <Canvas>
      <Content>
        <PageTitle>RANKING</PageTitle>
        {meta && user?.isSuperAdmin && (
          <MetaBar>
            <MetaText>
              {meta.lastSyncedAt
                ? `Última actualización: ${fmtLocalTime(meta.lastSyncedAt)}`
                : "Sin datos sincronizados"}
            </MetaText>
            <MetaText>
              {meta.playersWithData ?? 0} jugadores con datos
              {meta.playersWithoutData > 0 &&
                ` · ${meta.playersWithoutData} sin datos`}
            </MetaText>
          </MetaBar>
        )}
        {user?.isSuperAdmin && (
          <DebugToggle onClick={() => {
            if (!debugOpen) {
              setDebugOpen(true);
              setSyncHistoryLoading(true);
              const token = localStorage.getItem("access_token");
              axios.get(api_v3_sync_history(50), {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
              })
                .then((res) => setSyncHistory(res.data || []))
                .catch(() => setSyncHistory([]))
                .finally(() => setSyncHistoryLoading(false));
            } else {
              setDebugOpen(false);
            }
          }}>
            {debugOpen ? "OCULTAR DEBUG" : "DEBUG SYNC"}
          </DebugToggle>
        )}
        {debugOpen && user?.isSuperAdmin && (
          <DebugPanel>
            <DebugTitle>Sync Job History (últimas 50 ejecuciones)</DebugTitle>
            {syncHistoryLoading ? (
              <DebugEmpty>Cargando...</DebugEmpty>
            ) : syncHistory.length === 0 ? (
              <DebugEmpty>Sin registros</DebugEmpty>
            ) : (
              <DebugTable>
                  <thead>
                    <tr>
                      <DebugTh>Inicio</DebugTh>
                      <DebugTh>Duración</DebugTh>
                      <DebugTh>Estado</DebugTh>
                      <DebugTh>Total</DebugTh>
                      <DebugTh>OK</DebugTh>
                      <DebugTh>Err</DebugTh>
                      <DebugTh>Skip</DebugTh>
                      <DebugTh>Nunca</DebugTh>
                      <DebugTh>Stale</DebugTh>
                      <DebugTh></DebugTh>
                    </tr>
                  </thead>
                <tbody>
                  {syncHistory.map((h) => (
                    <tr key={h.id}>
                      <DebugTd>{`${fmtLocalTime(h.startedAt)} - ${h.finishedAt ? fmtLocalTime(h.finishedAt) : ""}`}</DebugTd>
                      <DebugTd>{runningElapsed(h)}</DebugTd>
                      <DebugTd>
                        <StatusBadge $status={h.status}>
                          {h.status === "completed" ? "✅" : h.status === "failed" ? "❌" : h.status === "canceled" ? "🛑" : "⏳"} {h.status}
                        </StatusBadge>
                      </DebugTd>
                      <DebugTd>{h.totalPlayers ?? "—"}</DebugTd>
                      <DebugTd>{h.successCount ?? "—"}</DebugTd>
                      <DebugTd>{h.errorCount ?? "—"}</DebugTd>
                      <DebugTd>{h.skippedCount ?? "—"}</DebugTd>
                      <DebugTd>{h.neverSynced ?? "—"}</DebugTd>
                      <DebugTd>{h.staleCount ?? "—"}</DebugTd>
                      <DebugTd>
                        {h.status === "running" && !h.killRequested ? (
                          <KillBtn onClick={() => handleKillJob(h.id)}>Kill</KillBtn>
                        ) : h.status === "running" && h.killRequested ? (
                          <KillBtn $danger onClick={() => handleForceCancel(h.id)}>Force</KillBtn>
                        ) : null}
                      </DebugTd>
                    </tr>
                  ))}
                </tbody>
              </DebugTable>
            )}
            {syncHistory.filter(h => h.status === "failed").length > 0 && (
              <DebugDetails>
                {syncHistory.filter(h => h.status === "failed").map(h => (
                  <DebugError key={h.id}>
                    <strong>{fmtLocalTime(h.startedAt)}</strong>: {h.errorLog}
                  </DebugError>
                ))}
              </DebugDetails>
            )}
            <UnlockBtn onClick={handleUnlock}>UNLOCK</UnlockBtn>
            <DeleteBtn onClick={handleDeleteHistory}>BORRAR HISTORIAL</DeleteBtn>
          </DebugPanel>
        )}

        <FilterBar ref={popoverRef}>
          <DiscBtn
            $active={showDisclaimer}
            onClick={() => setShowDisclaimer((s) => !s)}
            title="Cómo se calcula el ranking"
          >
            ⓘ
          </DiscBtn>

          <PopoverAnchor>
            <ChipBtn
              $active={selectedClanId}
              onClick={() => togglePopover("clan")}
            >
              {selectedClan ? (
                <>
                  <ChipIcon src={selectedClan.image} alt="" />
                  {selectedClan.name}
                </>
              ) : (
                "CLAN"
              )}
              <ChipArrow>▼</ChipArrow>
            </ChipBtn>
            {openPopover === "clan" && (
              <Popover>
                <PopoverGrid>
                  {clans.map((clan) => (
                    <PopoverItem
                      key={clan.clanId}
                      $selected={selectedClanId === clan.clanId}
                      onClick={() => handleClanClick(clan.clanId)}
                    >
                      <PopoverIcon src={clan.image} alt={clan.name} />
                      <PopoverName>{clan.name}</PopoverName>
                    </PopoverItem>
                  ))}
                </PopoverGrid>
              </Popover>
            )}
          </PopoverAnchor>

          <PopoverAnchor>
            <ChipBtn
              $active={selectedComunidadId}
              onClick={() => togglePopover("comunidad")}
            >
              {selectedComunidad ? (
                <>
                  <ChipIcon src={selectedComunidad.flagUrl} alt="" style={selectedComunidad.id === "valencia" ? { objectPosition: "15% center" } : {}} />
                  {selectedComunidad.name}
                </>
              ) : (
                "COMUNIDAD"
              )}
              <ChipArrow>▼</ChipArrow>
            </ChipBtn>
            {openPopover === "comunidad" && (
              <Popover>
                <PopoverGrid>
                  {comunidades.map((com) => (
                    <PopoverItem
                      key={com.id}
                      $selected={selectedComunidadId === com.id}
                      onClick={() => handleComunidadClick(com.id)}
                    >
                      <PopoverIcon src={com.flagUrl} alt={com.name} style={com.id === "valencia" ? { objectPosition: "15% center" } : {}} />
                      <PopoverName>{com.name}</PopoverName>
                    </PopoverItem>
                  ))}
                </PopoverGrid>
              </Popover>
            )}
          </PopoverAnchor>

          {hasFilters && (
            <ClearBtn onClick={handleClear}>LIMPIAR</ClearBtn>
          )}
        </FilterBar>

        {showDisclaimer && (
          <Explanation>
            El ranking se ordena por <strong>Rating</strong> por defecto.
            El Rating se calcula como la media de los últimos 50 partidas 1v1
            sumado al Elo máximo, dividido entre 2.
            Puedes cambiar el orden pinchando en las columnas.
            Los jugadores sin datos aparecen al final.
          </Explanation>
        )}
        <InsightsSection>
          <InsightCard>
            <InsightHeader>
              <InsightArrow $positive>↗</InsightArrow>
              <span>Subida 7d</span>
            </InsightHeader>
            <InsightList>
              {insights.gainers7d.length > 0 ? insights.gainers7d.map((player) => {
                const c = comunidades.find(x => x.id === player.comunidadAutonoma);
                return (
                <InsightRow key={player.playerId}>
                  <PlayerLink to={`/player/${player.playerId}`}>
                    <InsightName>{player.username}</InsightName>
                  </PlayerLink>
                  {c && <InsightFlag src={c.flagUrl} alt={c.name} />}
                  {player.clanImage && <InsightClanLogo src={player.clanImage} alt={player.clanName} />}
                  <InsightValue $positive>+{player.eloChange7d}</InsightValue>
                </InsightRow>
                );
              }) : <InsightEmpty>Sin datos</InsightEmpty>}
            </InsightList>
          </InsightCard>

          <InsightCard>
            <InsightHeader>
              <InsightArrow>↘</InsightArrow>
              <span>Bajada 7d</span>
            </InsightHeader>
            <InsightList>
              {insights.losers7d.length > 0 ? insights.losers7d.map((player) => {
                const c = comunidades.find(x => x.id === player.comunidadAutonoma);
                return (
                <InsightRow key={player.playerId}>
                  <PlayerLink to={`/player/${player.playerId}`}>
                    <InsightName>{player.username}</InsightName>
                  </PlayerLink>
                  {c && <InsightFlag src={c.flagUrl} alt={c.name} />}
                  {player.clanImage && <InsightClanLogo src={player.clanImage} alt={player.clanName} />}
                  <InsightValue>{player.eloChange7d}</InsightValue>
                </InsightRow>
                );
              }) : <InsightEmpty>Sin datos</InsightEmpty>}
            </InsightList>
          </InsightCard>

          <InsightCard>
            <InsightHeader>
              <InsightArrow $positive>↗</InsightArrow>
              <span>Subida 24h</span>
            </InsightHeader>
            <InsightList>
              {insights.gainer24h.length > 0 ? insights.gainer24h.map((player) => {
                const c = comunidades.find(x => x.id === player.comunidadAutonoma);
                return (
                <InsightRow key={player.playerId}>
                  <PlayerLink to={`/player/${player.playerId}`}>
                    <InsightName $strong>{player.username}</InsightName>
                  </PlayerLink>
                  {c && <InsightFlag src={c.flagUrl} alt={c.name} />}
                  {player.clanImage && <InsightClanLogo src={player.clanImage} alt={player.clanName} />}
                  <InsightValue $positive $large>+{player.eloChange24h}</InsightValue>
                </InsightRow>
                );
              }) : <InsightEmpty>Sin datos</InsightEmpty>}
            </InsightList>
          </InsightCard>

          <InsightCard>
            <InsightHeader>
              <InsightArrow>↘</InsightArrow>
              <span>Bajada 24h</span>
            </InsightHeader>
            <InsightList>
              {insights.loser24h.length > 0 ? insights.loser24h.map((player) => {
                const c = comunidades.find(x => x.id === player.comunidadAutonoma);
                return (
                <InsightRow key={player.playerId}>
                  <PlayerLink to={`/player/${player.playerId}`}>
                    <InsightName $strong>{player.username}</InsightName>
                  </PlayerLink>
                  {c && <InsightFlag src={c.flagUrl} alt={c.name} />}
                  {player.clanImage && <InsightClanLogo src={player.clanImage} alt={player.clanName} />}
                  <InsightValue $large>{player.eloChange24h}</InsightValue>
                </InsightRow>
                );
              }) : <InsightEmpty>Sin datos</InsightEmpty>}
            </InsightList>
          </InsightCard>
        </InsightsSection>
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <Th>
                  <Tooltip title="Posición actual en el ranking">#</Tooltip>
                </Th>
                <Th>
                  <Tooltip title="Nombre, clan y comunidad autónoma del jugador">Jugador</Tooltip>
                </Th>
                {SORT_FIELDS.map(({ key, label }) => (
                  <Th key={key} clickable onClick={() => handleSort(key)}>
                    <Tooltip title={TOOLTIPS[key]}>
                      {label}
                      {sortField === key && (
                        <SortArrow>{sortDir === "desc" ? " ▼" : " ▲"}</SortArrow>
                      )}
                    </Tooltip>
                  </Th>
                ))}
                <Th>
                  <Tooltip title="Tiempo desde la última partida registrada">Últ. partida</Tooltip>
                </Th>
                <Th>
                  <Tooltip title="Resultados de las últimas 10 partidas (verde: victoria, rojo: derrota, gris: sin datos)">
                    Últimas 10
                  </Tooltip>
                </Th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.map((entry, i) => {
                const comunidad = comunidades.find(c => c.id === entry.comunidadAutonoma);
                return (
                <tr key={entry.playerId}>
                  <Td>
                    <RankCell>
                      <RankNum>{i + 1}</RankNum>
                      {entry.rankChange != null && entry.rankChange !== 0 && (
                        <RankBadge $positive={entry.rankChange > 0}>
                          {entry.rankChange > 0 ? "+" : ""}{entry.rankChange}
                        </RankBadge>
                      )}
                    </RankCell>
                  </Td>
                  <Td>
                    <PlayerCell>
                      <Avatar
                        src={
                          entry.discordAvatarURL ||
                          "https://alexocampos.github.io/liga-espanhola-aoe2-static/assets/default-avatar.png"
                        }
                        alt={entry.username}
                        onError={(e) => {
                          if (!e.target.dataset.fallback) {
                            e.target.dataset.fallback = "1";
                            e.target.src = getInitialAvatar(entry.username);
                          }
                        }}
                      />
                      <PlayerInfo>
                        <PlayerLink to={`/player/${entry.playerId}`}>
                          <Username>{entry.username}</Username>
                        </PlayerLink>
                        <SubInfoRow>
                          {comunidad && (
                            <>
                              <SubFlag src={comunidad.flagUrl} alt={comunidad.name} />
                              <SubText>{comunidad.name}</SubText>
                            </>
                          )}
                          {entry.clanName && (
                            <>
                              {comunidad && <SubSep>|</SubSep>}
                              {entry.clanImage && <SubClanLogo src={entry.clanImage} alt={entry.clanName} />}
                              <SubText>{entry.clanName}</SubText>
                            </>
                          )}
                        </SubInfoRow>
                      </PlayerInfo>
                    </PlayerCell>
                  </Td>
                  <Td>
                    <EloValue>{entry.avgElo ?? "—"}</EloValue>
                  </Td>
                  <Td title={`Elo actual: ${entry.currentElo ?? "—"} | Elo máximo: ${entry.maxElo ?? "—"}`}>
                    <EloStack>
                      <EloCurrent>{entry.currentElo ?? "—"}</EloCurrent>
                      <EloMax>{entry.maxElo ?? "—"}</EloMax>
                    </EloStack>
                  </Td>
                  <Td>
                    <EloChangeSpan $positive={(entry.eloChange24h ?? 0) > 0} $zero={entry.eloChange24h == null || entry.eloChange24h === 0}>
                      {entry.eloChange24h != null ? (
                        <>{entry.eloChange24h > 0 ? "↗ +" : entry.eloChange24h < 0 ? "↘ -" : "±"}{entry.eloChange24h === 0 ? "0" : Math.abs(entry.eloChange24h)}</>
                      ) : "—"}
                    </EloChangeSpan>
                  </Td>
                  <Td>
                    <EloChangeSpan $positive={(entry.eloChange7d ?? 0) > 0} $zero={entry.eloChange7d == null || entry.eloChange7d === 0}>
                      {entry.eloChange7d != null ? (
                        <>{entry.eloChange7d > 0 ? "↗ +" : entry.eloChange7d < 0 ? "↘ -" : "±"}{entry.eloChange7d === 0 ? "0" : Math.abs(entry.eloChange7d)}</>
                      ) : "—"}
                    </EloChangeSpan>
                  </Td>
                  <Td>
                    <RelTime>{fmtRelativeTime(entry.lastGameDate)}</RelTime>
                  </Td>
                  <Td>
                    <Last10Row>
                      {entry.last10 && entry.last10.length > 0
                        ? entry.last10.map((r, j) => (
                            <ResultDot
                              key={j}
                              result={r.result}
                              title={`${r.result === "win" ? "Victoria" : r.result === "loss" ? "Derrota" : "Sin dato"} (${r.elo})`}
                            />
                          ))
                        : "—"}
                    </Last10Row>
                  </Td>
                </tr>
                );
              })}
            </tbody>
          </Table>
        </TableWrapper>
      </Content>
    </Canvas>
  );
};

const Canvas = styled.div`
  background: var(--v-canvas);
  min-height: 100vh;
  display: flex;
  justify-content: center;
  padding: 4rem 1.5rem;
`;

const Content = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const MetaBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const MetaText = styled.span`
  font-family: var(--v-font-mono);
  font-size: 11px;
  color: var(--v-fg-muted);
`;

const DiscBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: var(--v-surface);
  border: 1px solid
    ${({ $active }) =>
      $active ? "var(--v-mint)" : "rgba(255, 255, 255, 0.08)"};
  border-radius: 50%;
  color: ${({ $active }) =>
    $active ? "var(--v-mint)" : "var(--v-fg-muted)"};
  font-size: 14px;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: var(--v-mint);
    color: var(--v-mint);
    background: rgba(60, 255, 208, 0.04);
  }
`;

const Explanation = styled.p`
  font-family: var(--v-font-body);
  font-size: 12px;
  color: var(--v-fg-muted);
  line-height: 1.5;
  margin: 0;
  padding: 0.75rem 1rem;
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
`;

const PageTitle = styled.h1`
  font-family: var(--v-font-mono);
  font-size: 30px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 3px;
  background: linear-gradient(135deg, var(--v-mint), #7c5cfc);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
  line-height: 1.2;
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const PopoverAnchor = styled.div`
  position: relative;
`;

const ChipBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.75rem;
  background: var(--v-surface);
  border: 1px solid
    ${({ $active }) =>
      $active ? "var(--v-mint)" : "rgba(255, 255, 255, 0.08)"};
  border-radius: 20px;
  color: var(--v-fg);
  font-family: var(--v-font-mono);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
  white-space: nowrap;

  &:hover {
    border-color: var(--v-mint);
    background: rgba(60, 255, 208, 0.04);
  }
`;

const ChipIcon = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const ChipArrow = styled.span`
  font-size: 8px;
  color: var(--v-fg-muted);
  margin-left: 0.15rem;
`;

const Popover = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 100;
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 0.75rem;
  min-width: 360px;
  max-width: 500px;
  max-height: 380px;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
`;

const PopoverGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 0.5rem;
`;

const PopoverItem = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  padding: 0.75rem 0.4rem;
  border: 1px solid
    ${({ $selected }) =>
      $selected ? "var(--v-mint)" : "transparent"};
  border-radius: 12px;
  background: ${({ $selected }) =>
    $selected ? "rgba(60, 255, 208, 0.06)" : "transparent"};
  cursor: pointer;
  color: var(--v-fg);
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: var(--v-mint);
    background: rgba(60, 255, 208, 0.04);
  }
`;

const PopoverIcon = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
`;

const PopoverName = styled.span`
  font-family: var(--v-font-mono);
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: inherit;
  text-align: center;
  line-height: 1.2;
  word-break: break-word;
`;

const ClearBtn = styled.button`
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: var(--v-fg-muted);
  font-family: var(--v-font-mono);
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.4rem 0.75rem;
  border-radius: 20px;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: var(--v-mint);
    color: var(--v-mint);
  }
`;

const TableWrapper = styled.div`
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  padding: 0.5rem;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: var(--v-font-body);
`;

const Th = styled.th`
  text-align: left;
  padding: 0.75rem 1rem;
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--v-fg-muted);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  white-space: nowrap;
  cursor: ${(p) => (p.clickable ? "pointer" : "default")};
  user-select: none;
  transition: color 0.15s ease;

  &:hover {
    color: ${(p) => (p.clickable ? "var(--v-mint)" : "var(--v-fg-muted)")};
  }
`;

const Td = styled.td`
  padding: 0.65rem 1rem;
  font-size: 14px;
  color: var(--v-fg);
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  white-space: nowrap;
`;

const PlayerLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  transition: color 0.15s ease;

  &:hover {
    color: var(--v-mint);
  }
`;

const Avatar = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  background: rgba(255, 255, 255, 0.04);
`;

const Username = styled.span`
  font-weight: 500;
`;

const EloValue = styled.span`
  font-weight: 600;
  color: var(--v-mint);
`;

const RankCell = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
`;

const RankNum = styled.span`
  font-weight: 600;
`;

const RankBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  font-family: var(--v-font-mono);
  color: ${(p) => (p.$positive ? "var(--v-mint)" : "#ff4d6a")};
`;

const PlayerCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const PlayerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SubInfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 9px;
  color: var(--v-fg-muted);
  font-family: var(--v-font-mono);
  line-height: 1;
`;

const SubFlag = styled.img`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  object-fit: cover;
  flex-shrink: 0;
`;

const SubClanLogo = styled.img`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const SubText = styled.span`
  white-space: nowrap;
`;

const SubSep = styled.span`
  color: rgba(255, 255, 255, 0.15);
`;

const EloStack = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.3;
`;

const EloCurrent = styled.span`
  font-weight: 600;
  font-size: 14px;
`;

const EloMax = styled.span`
  font-size: 11px;
  color: var(--v-fg-muted);
`;

const EloChangeSpan = styled.span`
  font-family: var(--v-font-mono);
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  color: ${(p) =>
    p.$zero ? "var(--v-fg-muted)" : p.$positive ? "var(--v-mint)" : "#ff4d6a"};
`;

const RelTime = styled.span`
  font-size: 12px;
  color: var(--v-fg-muted);
  font-family: var(--v-font-mono);
`;

const SortArrow = styled.span`
  font-size: 9px;
`;

const Last10Row = styled.div`
  display: flex;
  gap: 3px;
  align-items: center;
`;

const ResultDot = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  background: ${(p) =>
    p.result === "win"
      ? "var(--v-mint)"
      : p.result === "loss"
        ? "#ff4d6a"
        : "rgba(255,255,255,0.08)"};
`;

const EmptyState = styled.p`
  font-family: var(--v-font-body);
  font-size: 14px;
  color: var(--v-fg-muted);
  text-align: center;
  padding: 4rem 1rem;
`;

const DebugToggle = styled.button`
  align-self: flex-start;
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--v-fg-muted);
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: var(--v-mint);
    color: var(--v-mint);
  }
`;

const DebugPanel = styled.div`
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 1rem;
  overflow-x: auto;
`;

const DebugTitle = styled.h3`
  font-family: var(--v-font-mono);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: var(--v-fg-muted);
  margin: 0 0 0.75rem 0;
`;

const DebugTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: var(--v-font-mono);
  font-size: 10px;
`;

const DebugTh = styled.th`
  text-align: left;
  padding: 0.4rem 0.5rem;
  font-weight: 600;
  color: var(--v-fg-muted);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  white-space: nowrap;
`;

const DebugTd = styled.td`
  padding: 0.35rem 0.5rem;
  color: var(--v-fg);
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  white-space: nowrap;
`;

const StatusBadge = styled.span`
  color: ${(p) =>
    p.$status === "completed"
      ? "var(--v-mint)"
      : p.$status === "failed"
        ? "#ff4d6a"
        : "var(--v-fg-muted)"};
`;

const DebugEmpty = styled.p`
  font-family: var(--v-font-body);
  font-size: 12px;
  color: var(--v-fg-muted);
  text-align: center;
  padding: 2rem 0;
`;

const DebugDetails = styled.div`
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

const UnlockBtn = styled.button`
  margin-top: 0.75rem;
  width: 100%;
  background: none;
  border: 1px solid #ffa500;
  color: #ffa500;
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(255, 165, 0, 0.1);
  }
`;

const DeleteBtn = styled.button`
  margin-top: 0.5rem;
  width: 100%;
  background: none;
  border: 1px solid #ff4d6a;
  color: #ff4d6a;
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(255, 77, 106, 0.1);
  }
`;

const KillBtn = styled.button`
  background: ${(p) => (p.$danger ? "rgba(255, 77, 106, 0.15)" : "none")};
  border: 1px solid #ff4d6a;
  color: #ff4d6a;
  font-family: var(--v-font-mono);
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(255, 77, 106, 0.25);
  }
`;

const DebugError = styled.p`
  font-family: var(--v-font-mono);
  font-size: 10px;
  color: #ff4d6a;
  margin: 0.25rem 0;
`;

const InsightsSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 0.75rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const InsightCard = styled.div`
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const InsightHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--v-fg-muted);
  padding-bottom: 0.35rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
`;

const InsightArrow = styled.span`
  font-size: 12px;
  line-height: 1;
  color: ${(p) => (p.$positive ? "var(--v-mint)" : "#ff4d6a")};
`;

const InsightList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const InsightRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 12px;
  line-height: 1.4;
  min-width: 0;
`;

const InsightPos = styled.span`
  font-family: var(--v-font-mono);
  font-size: 10px;
  color: var(--v-fg-muted);
  min-width: 14px;
  text-align: right;
  flex-shrink: 0;
`;

const InsightName = styled.span`
  font-weight: ${(p) => (p.$strong ? 600 : 500)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
`;

const InsightValue = styled.span`
  font-family: var(--v-font-mono);
  font-weight: 700;
  font-size: ${(p) => (p.$large ? "13px" : "11px")};
  white-space: nowrap;
  flex-shrink: 0;
  color: ${(p) => (p.$positive ? "var(--v-mint)" : "#ff4d6a")};
`;

const InsightEmpty = styled.span`
  font-size: 11px;
  color: var(--v-fg-muted);
  font-style: italic;
`;

const InsightFlag = styled.img`
  width: 10px;
  height: 10px;
  border-radius: 1px;
  object-fit: cover;
  flex-shrink: 0;
`;

const InsightClanLogo = styled.img`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

export default LeaderboardPage;
